import * as EventTracker from '@src/analytics/EventTracker';
import { useSpeakerHeadshot } from '@src/hooks/useSpeakerHeadshot';
import { useViewedQuotes } from '@src/hooks/useViewedQuotes';
import {
  useContentStudioUI,
  useDatoClient,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getImpactScore } from '@src/utility/impactScore';
import { downloadMediaFromSource } from '@src/utility/mediaGeneration';
import { formatStoryteller } from '@src/utility/shareableImages';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getPosterUrl, getRandomFileName } from '../../../utility/general';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import PhotoModalTop from '../../common/PhotoModalTop';
import PhotoModal from '../../common/PhotosModal';
import Modal from '../../Modal';
import ImpactScoreBadge from '../../sidepanel/ImpactScoreBadge';
import SpinningLoading from '../../SpinningLoading';
import QuoteCardKebabMenu from './QuoteCardKebabMenu';
import { QuoteThemeBadge } from './QuoteThemeBadge';
import SharableImagesModal from './SharableImagesModal';
import { usePreviewSupersample } from './usePreviewSupersample';
import { useQuoteCardRenderer } from './useQuoteCardRenderer';

import type { ImageKey, ImageWithType } from '../../../types.ts/general';
import type {
  Artifact,
  FileData,
  ShareableImageType,
} from '../../../types.ts/story';
import type { MediaFormat } from '../../common/FormatDropdown';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';
import type { Renderer } from '@src/renderer/Renderer';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

/** Duration (ms) for the highlight animation on recently saved quotes */
const HIGHLIGHT_DURATION_MS = 6000;

type Props = {
  card: ShareableImageType;
  isSaved: boolean;
  setLoadingStock: Dispatch<SetStateAction<boolean>>;
  compositionTemplates: CompositionTemplate[];
  onEditModalOpen: (modalData: {
    data: ShareableImageType;
    isSaved: boolean;
    compositionTemplate: CompositionTemplate;
    source: Record<string, any>;
    storyteller?: string;
  }) => void;
  registerCardRefs: (
    id: string,
    refs: {
      rendererRef: MutableRefObject<Renderer | null>;
      handleRerender: (source: Record<string, any>) => void;
      handleAddComposition: (composition: CompositionTemplate) => void;
      setCompositionTemplate: (composition: CompositionTemplate) => void;
    },
  ) => void;
  unregisterCardRefs: (id: string) => void;
};

const QuoteCard = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const datoClientStore = useDatoClient();
  const contentStudioUI = useContentStudioUI();

  const {
    id: cardId,
    quote,
    imagefile,
    source,
    storytellerName,
    speaker,
    metadata: rawMetadata,
    parentComposition,
  } = props.card;
  const metadata: Record<string, any> | undefined = useMemo(
    () =>
      typeof rawMetadata === 'string'
        ? (() => {
            try {
              return JSON.parse(rawMetadata);
            } catch {
              return undefined;
            }
          })()
        : rawMetadata,
    [rawMetadata],
  );
  const impactScore = getImpactScore(rawMetadata);
  const { isViewed, markViewed } = useViewedQuotes();
  const isNew = !isViewed(cardId);
  const { story } = videoCreator;
  const storyTeller = storytellerName || '';
  const [openMedia, toggleMedia] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [sharablePost, toggleShare] = useState<ShareableImageType | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const background = getPosterUrl(imagefile?.url || '', 1080, 1080, false);

  const isRecentlySaved = contentStudioUI.isRecentlySavedQuote(cardId);

  // Highlight recently saved quotes
  useEffect(() => {
    if (isRecentlySaved) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setIsHighlighted(true);
      const timer = setTimeout(() => {
        setIsHighlighted(false);
        contentStudioUI.dismissQuoteHighlight(cardId);
      }, HIGHLIGHT_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isRecentlySaved, cardId, contentStudioUI]);
  const defaultPullQuoteId =
    videoCreator.organization?.defaultPullQuoteTemplate?.id;

  const resolveCompositionTemplate = () =>
    props.compositionTemplates.find(
      template => template.id === parentComposition?.id,
    ) ||
    // Use organization default pull quote template if set
    (defaultPullQuoteId
      ? props.compositionTemplates.find(
          template => template.id === defaultPullQuoteId,
        )
      : undefined) ||
    // Prioritize "Workspace Templates" (non-global presets) over Arbor templates
    props.compositionTemplates?.find(template => !template.isGlobalPreset) ||
    props.compositionTemplates?.find(
      template =>
        template.isGlobalPreset && template.internalName.includes('Default'),
    ) ||
    props.compositionTemplates?.find(template => template.isGlobalPreset) ||
    props.compositionTemplates?.[0];

  const [selectedTemplate, setCompositionTemplate] =
    useState<CompositionTemplate>(resolveCompositionTemplate);
  // Use the list's copy: a save from any card replaces the template there by
  // id, while the one picked above is a snapshot from when it was picked.
  const compositionTemplate =
    props.compositionTemplates.find(t => t.id === selectedTemplate?.id) ??
    selectedTemplate;

  // The editor autosaves the card under whatever template it switched to, so
  // the card's saved template can change after mount. Follow it: Edit Quote
  // pairs this template with the card's saved source, and a mismatched pair
  // makes the next template switch carry quote/name/org over through the wrong
  // element positions (PLAT-12006).
  const [syncedParentCompositionId, setSyncedParentCompositionId] = useState(
    parentComposition?.id,
  );
  if (parentComposition?.id !== syncedParentCompositionId) {
    setSyncedParentCompositionId(parentComposition?.id);
    setCompositionTemplate(resolveCompositionTemplate());
  }

  const { contentStudioEnableShareableImageSave } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const updateImage = (
    image: Artifact | undefined,
    uploadId: string,
    url: string,
  ) => {
    return {
      ...(image || {}),
      id: uploadId,
      url,
      responsiveImage: {
        ...(image?.responsiveImage || {}),
        srcSet: url,
      },
    };
  };

  const speakerHeadshotUrl = useSpeakerHeadshot({
    speakerId: speaker?.id,
    speakerName: formatStoryteller(storyTeller).name,
    storyId: videoCreator.story?.id,
    datoClientStore,
  });

  // Initialize Creatomate renderer using custom hook
  const { rendererContainerRef, rendererRef, replaceCompositionElement } =
    useQuoteCardRenderer({
      text: quote,
      background,
      name: formatStoryteller(storyTeller).name,
      org: formatStoryteller(storyTeller).org,
      headshot: speakerHeadshotUrl,
      compositionTemplate: compositionTemplate,
      quoteCardSource: source || {},
      hoverOverlay: true,
    });

  const { footprintRef, width, height, scale } = usePreviewSupersample();

  const replaceAction = async () => {
    if (selectedImage) {
      try {
        const isInDato = selectedImage.url.startsWith(
          'https://www.datocms-assets.com',
        );
        let url = selectedImage.url;
        let uploadId: string = selectedImage.id;

        if (!isInDato) {
          props.setLoadingStock(true);
          toggleMedia(false);
          const fileName = getRandomFileName(quote);

          const newPhotoData: FileData & { fileName: string } = {
            type: 'stock',
            url: selectedImage.url,
            fileName,
            alt: quote,
            title: quote,
          };

          const newUpload =
            await datoClientStore.assetRepository?.uploadFile(newPhotoData);
          if (newUpload) {
            url = newUpload.url;
            uploadId = newUpload.id;
          }
        }

        if (uploadId && url) {
          if (props.isSaved) {
            const saved = (story?.shareableImages || []).map(s => {
              if (s.id === cardId) {
                return {
                  ...s,
                  imagefile: updateImage(s.imagefile, uploadId, url),
                } as ShareableImageType;
              }
              return s;
            });
            runInAction(() => {
              videoCreator.story!.shareableImages = saved;
            });
          } else {
            const unsaved = (videoCreator.unsavedShareableImages || []).map(
              o => {
                if (o.id === cardId) {
                  return {
                    ...o,
                    imagefile: updateImage(o.imagefile, uploadId, url),
                  };
                }
                return o;
              },
            );
            runInAction(() => {
              videoCreator.unsavedShareableImages = unsaved as Omit<
                ShareableImageType,
                '_allReferencingSharedContents'
              >[];
            });
          }
        }
      } catch (error) {
        console.log('Error occurred in replace action: ', error);
      } finally {
        toggleMedia(false);
        props.setLoadingStock(false);
      }
    }
  };

  const editShareableImage = async () => {
    markViewed(cardId);
    props.onEditModalOpen({
      data: {
        id: cardId,
        imagefile,
        quote: quote,
        storytellerName: storytellerName,
        speaker,
        metadata: metadata || {},
        source: source || {},
      },
      isSaved: props.isSaved,
      compositionTemplate,
      source: source || {},
      storyteller: storyTeller,
    });
  };

  const downloadShareableImage = async (
    e: React.MouseEvent<HTMLButtonElement>,
    formatOverride?: MediaFormat,
  ) => {
    await downloadMediaFromSource(e, {
      imageSource: rendererRef.current?.getSource(),
      itemId: cardId,
      storyTitle: story?.title,
      quoteText: quote,
      formatOverride,
      onDownloadStart: () => {
        setIsDownloading(true);
      },
      onDownloadEnd: () => setIsDownloading(false),
      onDownloadSuccess: outputFormat => {
        EventTracker.trackPullQuoteDownloaded({
          story_id: story?.id,
          card_id: cardId,
          format: outputFormat,
        });
      },
    });
  };

  const handleShareClick = () => {
    toggleShare({
      id: cardId,
      imagefile,
      storytellerName: storytellerName,
      quote: quote,
      _allReferencingSharedContents: props.card._allReferencingSharedContents,
      _allReferencingCaptions: props.card._allReferencingCaptions!,
      source: rendererRef.current?.getSource() || {},
    });
  };

  const deleteShareableImage = async () => {
    if (!story) return;

    try {
      setIsDeleteLoading(true);

      if (props.isSaved) {
        videoCreator.story!.shareableImages = story?.shareableImages.filter(
          s => s.id !== cardId,
        );

        await videoCreator.updateStory(videoCreator.story!);
      } else {
        videoCreator.unsavedShareableImages = (
          videoCreator.unsavedShareableImages || []
        ).filter(o => o.id !== cardId);
      }
      videoCreator.showToast({
        state: 'success',
        message: 'Pull quote deleted successfully',
        delay: 3000,
      });
    } catch (error) {
      console.log('Error occurred in deleting sharable image: ', error);
      videoCreator.showToast({
        state: 'error',
        message: 'Error occurred in deleting sharable image',
        delay: 3000,
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleRerender = (source: Record<string, any>) => {
    rendererRef.current?.setSource(source);
  };

  const handleAddComposition = (composition: CompositionTemplate) => {
    setCompositionTemplate(composition);
    replaceCompositionElement(composition);
  };

  // Register/unregister refs with parent
  useEffect(() => {
    props.registerCardRefs(cardId, {
      rendererRef,
      handleRerender,
      handleAddComposition,
      setCompositionTemplate,
    });

    return () => {
      props.unregisterCardRefs(cardId);
    };
  }, [
    cardId,
    props.registerCardRefs,
    props.unregisterCardRefs,
    handleRerender,
    handleAddComposition,
    setCompositionTemplate,
  ]);

  return (
    <Main
      ref={cardRef}
      $isHighlighted={isHighlighted}
      data-testid={`quote-card-${cardId}`}
      onClick={editShareableImage}
    >
      <TopActions>
        <TopActionsLeft>
          {impactScore != null && Object.keys(impactScore).length > 0 && (
            <ImpactScoreBadgeWrapper>
              <ImpactScoreBadge
                impactScore={impactScore}
                id={`quote-${cardId}`}
              />
            </ImpactScoreBadgeWrapper>
          )}
          {(metadata?.theme || metadata?.customPrompt) && (
            <QuoteThemeBadge metadata={metadata} cardId={cardId} />
          )}
          {isNew && <NewBadge>New</NewBadge>}
        </TopActionsLeft>
        {contentStudioEnableShareableImageSave && (
          <QuoteCardKebabMenu
            data-testid={`quote-card-menu-${cardId}`}
            onEdit={editShareableImage}
            onDownload={downloadShareableImage}
            onShare={handleShareClick}
            onDelete={deleteShareableImage}
          />
        )}
      </TopActions>
      <Content ref={footprintRef}>
        <RendererContainer
          ref={rendererContainerRef}
          style={{
            width,
            height,
            transform: `scale(${scale})`,
          }}
        />
        <HoverOverlay className="hover-overlay">
          <ViewQuoteButton
            type="button"
            onClick={e => {
              e.stopPropagation();
              editShareableImage();
            }}
          >
            <span>Edit Quote</span>
            <ArrowRightIcon />
          </ViewQuoteButton>
        </HoverOverlay>
        {isDeleteLoading && (
          <SpinningLoading text="Deleting image…" spinnerSize="32px" />
        )}
        {isDownloading && (
          <SpinningLoading text="Downloading image…" spinnerSize="32px" />
        )}

        {openMedia && (
          <Modal
            isOpen={true}
            onClose={() => toggleMedia(false)}
            paddingHorizontal="0"
          >
            <PhotoModal
              TopComponent={props => (
                <PhotoModalTop
                  {...props}
                  replaceAction={replaceAction}
                  title="social posts"
                  isSelected={!!selectedImage}
                  selectedImage={selectedImage}
                />
              )}
              otherFields={['stock', 'org_photos']}
              onCloseSelf={() => toggleMedia(false)}
              selectedImageUrl={selectedImage?.url || null}
              setSelectedImage={setSelectedImage}
            />
          </Modal>
        )}

        {sharablePost && (
          <SharableImagesModal
            sharablePost={sharablePost}
            toggleShare={toggleShare}
            compositionTemplate={compositionTemplate}
          />
        )}
      </Content>
    </Main>
  );
});

export default QuoteCard;

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const highlightFade = keyframes`
  0% {
    border-color: var(--primary);
  }
  70% {
    border-color: var(--primary);
  }
  100% {
    border-color: transparent;
  }
`;

const Main = styled.div<{ $isHighlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  background-color: var(--surface);
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: box-shadow var(--transition-normal) ease;
  ${props =>
    props.$isHighlighted &&
    css`
      animation: ${highlightFade} ${HIGHLIGHT_DURATION_MS}ms ease-out forwards;
    `}

  &:hover {
    box-shadow: var(--elevation-sm);
  }
`;

const ImpactScoreBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const Content = styled.div<{ background?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--background);
  flex-direction: column;

  width: 100%;
  min-width: 220px;
  aspect-ratio: 1 / 1;

  ${props =>
    props.background &&
    css`
      background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%),
        url(${props.background});

      background-size: cover;
      background-position: center center;
    `}

  &:hover .hover-overlay {
    opacity: 1;
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal) ease;
  z-index: 1;
`;

const ViewQuoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 8px 12px;
`;

const TopActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
  background-color: var(--surface-elevated);
  color: var(--text-secondary);
`;

// Sized/scaled inline via usePreviewSupersample so the iframe rasterizes at a
// higher resolution than the card footprint, then CSS-downscales to fit.
const RendererContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  overflow: hidden;
  border-radius: 0;
  background-color: var(--surface);
`;
